import { createPlan, editPlan, getPlanDetail } from '../../../api/planApi'
import InputColor from '../../../components/input-color/input-color'
import { LocaleOptions } from '../../../helpers/constant'
import translate from '../../../i18n'
import { LocaleType, TestSideEnums } from '../../../share-types'
import { ReduxStore } from '../../../store'
import { City, CityTranslation } from '../../../store/city/reducer'
import {
  IPlanFormData,
  IPlanTranslation,
  PlanStatusEnums,
  PlanTypeEnums,
  SubscriptionRenewalModeEnums,
} from '../share-type'
import {
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  notification,
} from 'antd'
import { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { compose } from 'redux'

const defaultTranslations: IPlanTranslation[] = [
  { locale: LocaleType.zh_CN, name: '' },
  { locale: LocaleType.en, name: '' },
]

const planTypeOptions = Object.values(PlanTypeEnums).map(value => ({
  label: translate(`plan.type.${value}`),
  value,
  disabled: ![PlanTypeEnums.FIXED, PlanTypeEnums.SUBSCRIPTION].includes(value),
}))

const subscriptionRenewalModeOptions = Object.values(SubscriptionRenewalModeEnums).map(value => ({
  label: translate(`plan.subscriptionRenewalMode.${value}`),
  value,
}))

const PlanForm = (props: ConnectedProps<typeof withConnect>) => {
  const { activeCities, locale } = props
  const [form] = Form.useForm<IPlanFormData>()
  const params = useParams()
  const navigate = useNavigate()
  const [planId] = useState<string | undefined>(params?.id)
  const planType = Form.useWatch('planType', form)
  const subscriptionRenewalMode = Form.useWatch('subscriptionRenewalMode', form)
  const isSubscription = planType === PlanTypeEnums.SUBSCRIPTION

  useEffect(() => {
    if (isSubscription) {
      form.setFieldValue('isTopUp', false)
      if (!form.getFieldValue('subscriptionRenewalMode')) {
        form.setFieldValue('subscriptionRenewalMode', SubscriptionRenewalModeEnums.AUTO_RENEW)
      }
    } else {
      form.setFieldValue('subscriptionRenewalMode', undefined)
      form.setFieldValue('wechatContractPlanId', undefined)
    }
  }, [form, isSubscription])

  useEffect(() => {
    if (subscriptionRenewalMode === SubscriptionRenewalModeEnums.ONE_TIME) {
      form.setFieldValue('wechatContractPlanId', undefined)
    }
  }, [form, subscriptionRenewalMode])

  useEffect(() => {
    if (planId) {
      getPlanDetail(Number(planId)).then(res => {
        form.setFieldsValue({
          ...res,
          price: res.price == null ? undefined : res.price / 100,
          originalPrice: res.originalPrice == null ? undefined : res.originalPrice / 100,
          translations: res.translations?.length ? res.translations : defaultTranslations,
        })
      })
    }
  }, [form, planId])

  const onFinish = (formData: IPlanFormData) => {
    const requestData: IPlanFormData = {
      ...formData,
      price: Math.round(Number(formData.price) * 100),
      originalPrice:
        formData.originalPrice === undefined || formData.originalPrice === null
          ? undefined
          : Math.round(Number(formData.originalPrice) * 100),
      marketingHeadingBgColor: formData.marketingHeadingBgColor || undefined,
      translations: formData.translations.filter(
        translation => translation.locale && translation.name,
      ),
    }

    const request = planId ? editPlan(Number(planId), requestData) : createPlan(requestData)

    request
      .then(res => {
        notification.success({ title: translate('success') })
        navigate(`/plans/${res.id}`)
      })
      .catch(error => {
        notification.error({
          title: translate('apiError'),
          description: error.message ?? '',
        })
      })
  }

  return (
    <Card>
      <Form
        name="plan-form"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 12 }}
        onFinish={onFinish}
        autoComplete="off"
        form={form}
        className="resource-form"
        initialValues={{
          planType: PlanTypeEnums.FIXED,
          status: PlanStatusEnums.ACTIVE,
          miniProgramSelectable: false,
          isTopUp: false,
          isNewUserOnly: false,
          translations: defaultTranslations,
        }}
      >
        <Form.Item
          name="name"
          label={translate('name')}
          rules={[{ required: true, message: translate('name.message') }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="externalId" label={translate('plan.externalId')}>
          <Input />
        </Form.Item>
        <Form.Item name="cityId" label={translate('plan.city')}>
          <Select allowClear>
            {activeCities.map((city: City) => {
              const translation = city.translations.find(
                (item: CityTranslation) => item.locale === locale,
              )
              return (
                <Select.Option key={city.id} value={city.id}>
                  {translation?.name}
                </Select.Option>
              )
            })}
          </Select>
        </Form.Item>
        <Form.Item name="description" label={translate('plan.description')}>
          <Input.TextArea rows={3} />
        </Form.Item>
        <Form.Item name="planType" label={translate('plan.type')} rules={[{ required: true }]}>
          <Select disabled={Boolean(planId)} options={planTypeOptions} />
        </Form.Item>
        <Form.Item label={translate('plan.price')}>
          <Space.Compact style={{ width: '100%' }}>
            <Input disabled value="¥" style={{ width: 40 }} />
            <Form.Item name="price" rules={[{ required: true }]} noStyle>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
        <Form.Item label={translate('plan.originalPrice')} name="originalPrice">
          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="credits" label={translate('plan.credits')} rules={[{ required: true }]}>
          <InputNumber min={0} precision={0} style={{ width: '100%' }} />
        </Form.Item>
        {isSubscription ? (
          <>
            <Form.Item label={translate('plan.billingPeriodUnit')}>
              <Select value="MONTH" disabled options={[{ label: 'MONTH', value: 'MONTH' }]} />
            </Form.Item>
            <Form.Item label={translate('plan.billingPeriodCount')}>
              <InputNumber value={1} disabled style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="subscriptionRenewalMode"
              label={translate('plan.subscriptionRenewalMode')}
              rules={[{ required: true }]}
            >
              <Select options={subscriptionRenewalModeOptions} />
            </Form.Item>
            {subscriptionRenewalMode === SubscriptionRenewalModeEnums.AUTO_RENEW && (
              <Form.Item
                name="wechatContractPlanId"
                label={translate('plan.wechatContractPlanId')}
                rules={[
                  { required: true, message: translate('plan.wechatContractPlanId.required') },
                ]}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </>
        ) : (
          <Form.Item
            name="validityPeriod"
            label={translate('plan.validityPeriod')}
            rules={[{ required: true }]}
          >
            <InputNumber min={0} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item
          name="miniProgramSelectable"
          label={translate('plan.miniProgramSelectable')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {!isSubscription && (
          <Form.Item name="isTopUp" label={translate('plan.isTopUp')} valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item
          name="isNewUserOnly"
          label={translate('plan.isNewUserOnly')}
          valuePropName="checked"
        >
          <Switch disabled={isSubscription} />
        </Form.Item>
        <Form.Item name="marketingHeadingBgColor" label={translate('plan.marketingHeadingBgColor')}>
          <InputColor />
        </Form.Item>
        <Form.Item name="testSide" label={translate('plan.testSide')}>
          <Select
            allowClear
            options={Object.values(TestSideEnums).map(value => ({ label: value, value }))}
          />
        </Form.Item>
        <Form.Item name="status" label={translate('plan.status')} rules={[{ required: true }]}>
          <Select
            options={[
              { label: translate('plan.status.active'), value: PlanStatusEnums.ACTIVE },
              { label: translate('plan.status.archived'), value: PlanStatusEnums.ARCHIVED },
            ]}
          />
        </Form.Item>

        <Divider>{translate('plan.translations')}</Divider>
        <Form.List name="translations">
          {fields => (
            <>
              {fields.map(field => (
                <Space key={field.key} orientation="vertical" style={{ width: '100%' }}>
                  <Form.Item name={[field.name, 'id']} hidden>
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'locale']}
                    label={translate('plan.translationLocale')}
                    rules={[{ required: true }]}
                  >
                    <Select disabled options={LocaleOptions} />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'name']}
                    label={translate('plan.translationName')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'marketingHeading']}
                    label={translate('plan.marketingHeading')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'marketingSubHeading']}
                    label={translate('plan.marketingSubHeading')}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'appMarketingTagline1']}
                    label={translate('plan.benefitDescription', { index: 1 })}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'appMarketingTagline2']}
                    label={translate('plan.benefitDescription', { index: 2 })}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, 'appMarketingTagline3']}
                    label={translate('plan.benefitDescription', { index: 3 })}
                  >
                    <Input />
                  </Form.Item>
                </Space>
              ))}
            </>
          )}
        </Form.List>
        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            {translate('submit')}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  )
}

const mapStateToProps = (state: ReduxStore) => ({
  activeCities: state.cities.activeCities,
  locale: state.global.locale,
})

const withConnect = connect(mapStateToProps)

export default compose(withConnect)(PlanForm)
